import { useState } from 'react';
import type { AuditEvent } from '@gatecheckpro/contracts';
import { Card, EmptyState, Field, LoadingRegion, Notice } from '@gatecheckpro/design-system';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Platform-wide audit.
 *
 * Reading it is itself audited under the reader's name. An investigator's access
 * to evidence has to be part of the evidence, or the trail has a blind spot
 * exactly where it matters most (SDAD Section 39).
 */
export function AuditPage() {
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState('');

  const query = new URLSearchParams({ limit: '100' });
  if (applied.length > 0) query.set('action', applied);
  const { resource } = useResource<{ events: AuditEvent[] }>(
    `/api/v1/ops/audit?${query.toString()}`,
  );

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-page-header">
        <span className="gc-eyebrow">Security operations</span>
        <h1>Audit trail</h1>
        <p className="gc-lede">
          Append-only. The database refuses to change or delete a record here, and refuses to
          truncate the table.
        </p>
      </div>

      <Notice tone="caution" title="Your reads are recorded too">
        Every query on this page appears in the trail under your name, with what you filtered on and
        how many records came back.
      </Notice>

      <Card title="Filter">
        <form
          className="gc-row"
          onSubmit={(event) => {
            event.preventDefault();
            setApplied(action.trim());
          }}
        >
          <div style={{ flex: '1 1 20rem' }}>
            <Field
              label="Action"
              placeholder="organization.verification_decided"
              hint="Exact match. Leave empty for everything."
              value={action}
              onChange={(event) => setAction(event.target.value)}
            />
          </div>
          <button type="submit" className="gc-button gc-button--primary">
            Apply
          </button>
        </form>
      </Card>

      {resource.status === 'loading' && <LoadingRegion label="Loading audit records" />}
      {resource.status === 'failed' && <FailureNotice error={resource.error} />}

      {resource.status === 'loaded' &&
        (resource.data.events.length === 0 ? (
          <EmptyState title="No records match">
            Nothing in the trail matches that filter.
          </EmptyState>
        ) : (
          <div className="gc-table-scroll">
            <table className="gc-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Action</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Resource</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {resource.data.events.map((event) => (
                  <tr key={event.auditEventId}>
                    <td className="gc-mono">{new Date(event.occurredAt).toLocaleString()}</td>
                    <td className="gc-mono">{event.action}</td>
                    <td>{event.actorDisplayName ?? '—'}</td>
                    <td>
                      <div className="gc-stack gc-stack--tight">
                        <span>{event.resourceType}</span>
                        {event.resourceId !== null && (
                          <span className="gc-muted gc-mono">{event.resourceId.slice(0, 8)}…</span>
                        )}
                      </div>
                    </td>
                    <td className="gc-muted">{event.reasonCode ?? '—'}</td>
                    <td className="gc-muted gc-mono">{event.sourceIp ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
